import { useSettings } from "../../../../contexts/SettingsManager/SettingsManager";
import type {
  ProcessGroup,
  DataDumpAggregateType,
  Process,
} from "../../../../types/types";
import GroupTileContent from "./GroupTileContent";
import {
  useVisibleGroups,
  useFocusRange,
  useDataDumpGroupTile,
  useSelectedShift,
  useCalcPreferredMetric,
  useLoadWhenValuesChange,
} from "../../../../utils/customHooks/genericHooks";
import {
  addRollups,
  getPredictedFutureMetric,
} from "../../../../utils/metrics";
import { useSelector } from "store/useSelector";
import { useShouldFetchDataDumpForGroupTiles } from "components/DataGetter/DataGetter";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useState, useEffect, startTransition, useMemo } from "react";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

type GroupTileProps = {
  groupSortIdx: string;
  shortName: string;
  output: number | null;
  outputLabel: string | null;
  showCountNotMetricOnGroupTile: boolean;
  isIdleUntagged: boolean | null;
  tileWidth?: number;
  callStart?: number | null;
  processes: Process[];
  summaryGroup: string;
};

const GroupTile = ({
  groupSortIdx,
  shortName,
  output,
  outputLabel,
  showCountNotMetricOnGroupTile,
  isIdleUntagged,
  tileWidth,
  callStart,
  processes,
  summaryGroup,
}: GroupTileProps) => {
  const focusRange = useFocusRange();
  const visibleGroups = useVisibleGroups();
  const rollupsMetricByProcess = useSelector(
    state => state.legacy.rollupsMetricByProcess
  );
  const selectedShift = useSelectedShift();
  const {
    isLargeTiles,
    isTilesPageFullDay,
    preferredMetric,
    isPredictedFutureMetric,
    estimatedFutureMetricPercent,
  } = useSettings();
  const shouldUseDataDumpsForGroupTiles = useShouldFetchDataDumpForGroupTiles();

  const selectedProcessIdsForGroup = useSelectedProcessesIdsForGroup(
    shortName,
    processes
  );

  const visibleGroup: ProcessGroup | undefined = useMemo(
    () => visibleGroups[Number(groupSortIdx)],
    [groupSortIdx, visibleGroups]
  );

  const rollupMetricForGroupTile = useMemo(
    () =>
      focusRange &&
      visibleGroup &&
      addRollups(
        rollupsMetricByProcess,
        visibleGroup.processes?.filter(p =>
          selectedProcessIdsForGroup.includes(p.id)
        )
      ),
    [
      focusRange,
      rollupsMetricByProcess,
      selectedProcessIdsForGroup,
      visibleGroup,
    ]
  );

  const calcPreferredMetric = useCalcPreferredMetric();

  const dataDumpObjForGroup = useDataDumpGroupTile(summaryGroup);

  const dataDumpData = useMemo(
    () =>
      shouldUseDataDumpsForGroupTiles
        ? dataDumpObjForGroup
        : ({
            ...dataDumpObjForGroup,
            netOutput: visibleGroup?.output,
          } as DataDumpAggregateType),
    [dataDumpObjForGroup, shouldUseDataDumpsForGroupTiles, visibleGroup]
  );

  const shouldShowPredictedMetric = useMemo(
    () => isPredictedFutureMetric && selectedShift?.range?.lt > Date.now(),
    [isPredictedFutureMetric, selectedShift]
  );

  const isDataAvailable = useMemo(
    () =>
      Boolean(
        shouldUseDataDumpsForGroupTiles
          ? dataDumpData
          : rollupMetricForGroupTile
      ),
    [dataDumpData, rollupMetricForGroupTile, shouldUseDataDumpsForGroupTiles]
  );

  const metricPercent = useMemo(
    () =>
      !isDataAvailable
        ? undefined
        : shouldShowPredictedMetric
          ? getPredictedFutureMetric({
              isOver24Hours: isTilesPageFullDay,
              dataDumpKey: preferredMetric,
              selectedShift,
              rollupDisplay: rollupMetricForGroupTile,
              numProcesses: visibleGroup?.processes?.length || 1,
              estimatedFutureMetricPercent,
              dataDumpsObjForFullRange: dataDumpData,
              isMidgardDDAEnabled: shouldUseDataDumpsForGroupTiles,
              getNumbers: true,
            })
          : (rollupMetricForGroupTile || dataDumpObjForGroup) &&
            calcPreferredMetric(rollupMetricForGroupTile, dataDumpObjForGroup),
    [
      calcPreferredMetric,
      dataDumpObjForGroup,
      dataDumpData,
      estimatedFutureMetricPercent,
      isDataAvailable,
      isTilesPageFullDay,
      preferredMetric,
      rollupMetricForGroupTile,
      selectedShift,
      shouldShowPredictedMetric,
      visibleGroup?.processes?.length,
      shouldUseDataDumpsForGroupTiles,
    ]
  );

  const outputCount = useMemo(
    () => dataDumpData?.netOutput || output,
    [dataDumpData, output]
  );

  const isStorybook = useIsStorybook();
  const loading = useLoadWhenValuesChange({
    valuesWhichChangeArr: [focusRange, metricPercent],
    loadingInterval: 4 * 1000,
    isInitiallyLoading: !isStorybook,
    componentNameForDebugging: "GroupMetrics",
  });

  const [loadedOnce, setLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) {
      startTransition(() => {
        setLoadedOnce(true);
      });
    }
  }, [loading]);
  return (
    <GroupTileContent
      isLargeTiles={isLargeTiles}
      tileWidth={tileWidth}
      callStart={callStart}
      showCountNotMetricOnGroupTile={showCountNotMetricOnGroupTile}
      isTilesPageFullDay={isTilesPageFullDay}
      output={outputCount}
      outputLabel={outputLabel}
      metricPercent={!loadedOnce ? undefined : metricPercent}
      isIdleUntagged={isIdleUntagged}
      shortName={shortName}
      isCustomerWhoUsesGroupTileDataDump={shouldUseDataDumpsForGroupTiles}
    />
  );
};
export default GroupTile;

export function useSelectedProcessesIdsForGroup(
  shortName: string,
  processes: Process[]
) {
  const [
    {
      general: { isAllGroupSerial },
    },
  ] = useFlightConfig();
  const isAllGroup = ["all", "all machines"].includes(shortName.toLowerCase());
  const shouldFilter = isAllGroup ? isAllGroupSerial : true;

  return (
    shouldFilter ? processes.filter(process => process.isSelected) : processes
  ).map(p => p.id);
}
